// DashboardFiltersBar.jsx - Grafana-style dashboard-level filter bar
import React from 'react';
import { Settings, Plus, X, RefreshCw, Filter, Share2 } from 'lucide-react';
import { FILTER_OPERATORS } from './constants';

export default function DashboardFiltersBar({
  variables,
  variableValues,
  variableOptions,
  variableOptionsLoading,
  onVariableChange,
  onRefreshVariables,
  adhocFilters,
  adhocColumnOptions,
  onAddAdhocFilter,
  onUpdateAdhocFilter,
  onRemoveAdhocFilter,
  onOpenVariableManager,
  onShare,
  darkMode,
  theme
}) {
  const hasVariables = variables && variables.length > 0;
  const hasAdhocFilters = adhocFilters && adhocFilters.length > 0;

  const selectStyle = {
    padding: '3px 6px',
    background: theme.card,
    border: `1px solid ${theme.border}`,
    borderRadius: '5px',
    color: theme.text,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    height: '26px',
    colorScheme: darkMode ? 'dark' : 'light'
  };

  const inputStyle = {
    padding: '3px 6px',
    background: theme.card,
    border: `1px solid ${theme.border}`,
    borderRadius: '5px',
    color: theme.text,
    fontSize: '12px',
    outline: 'none',
    width: '90px',
    height: '26px',
    colorScheme: darkMode ? 'dark' : 'light'
  };

  return (
    <div style={{
      background: theme.card,
      borderBottom: `1px solid ${theme.border}`,
      padding: '6px 16px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',
      flexShrink: 0,
      minHeight: '42px'
    }}>
      {/* Variables gear / manage button (admin only) */}
      {onOpenVariableManager && (
      <button
        onClick={onOpenVariableManager}
        title="Manage dashboard variables"
        style={{
          padding: '4px 8px',
          background: 'transparent',
          border: `1px solid ${theme.border}`,
          borderRadius: '6px',
          color: theme.textMuted,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
          fontWeight: '600',
          flexShrink: 0,
          height: '26px'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = '#6366f1';
          e.currentTarget.style.color = '#6366f1';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = theme.border;
          e.currentTarget.style.color = theme.textMuted;
        }}
      >
        <Settings size={12} />
        Variables
      </button>
      )}

      {/* Variable dropdowns / sliders */}
      {hasVariables && variables.map(variable => {
        // What-if slider variable — renders as a range input, not a dropdown.
        if (variable.type === 'slider') {
          const min = Number(variable.min ?? 0);
          const max = Number(variable.max ?? 100);
          const step = Number(variable.step ?? 1);
          const rawVal = variableValues[variable.id];
          const val = Number(rawVal ?? variable.defaultValue ?? min);
          return (
            <div key={variable.id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '2px 8px', borderRadius: 6,
              background: darkMode ? 'rgba(139,92,246,0.08)' : 'rgba(124,58,237,0.06)',
              border: `1px solid ${darkMode ? 'rgba(139,92,246,0.3)' : 'rgba(124,58,237,0.25)'}`
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, flexShrink: 0 }}>
                {variable.label || variable.name}:
              </span>
              <input
                type="range"
                min={min} max={max} step={step} value={val}
                onChange={e => onVariableChange(variable.id, e.target.value)}
                style={{ width: 120, accentColor: '#8b5cf6', cursor: 'pointer' }}
              />
              <span style={{
                minWidth: 40, fontSize: 12, fontWeight: 700, color: theme.text,
                fontVariantNumeric: 'tabular-nums', textAlign: 'right'
              }}>{val}{variable.unit || ''}</span>
            </div>
          );
        }
        // Standard dropdown variable (existing behaviour)
        return (
          <div key={variable.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{
              fontSize: '11px',
              fontWeight: '600',
              color: theme.textMuted,
              flexShrink: 0
            }}>
              {variable.label || variable.name}:
            </span>
            <select
              value={variableValues[variable.id] || ''}
              onChange={e => onVariableChange(variable.id, e.target.value)}
              disabled={variableOptionsLoading[variable.id]}
              style={{
                ...selectStyle,
                minWidth: '110px',
                opacity: variableOptionsLoading[variable.id] ? 0.6 : 1
              }}
            >
              <option value="">All</option>
              {(variableOptions[variable.id] || []).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
      })}

      {/* Divider between variables and ad-hoc filters */}
      {(hasVariables && hasAdhocFilters) && (
        <div style={{ width: '1px', height: '20px', background: theme.border, alignSelf: 'center', flexShrink: 0 }} />
      )}

      {/* Ad-hoc filter pills */}
      {hasAdhocFilters && adhocFilters.map(filter => (
        <div key={filter.id} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          background: darkMode ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)',
          border: '1px solid rgba(99,102,241,0.3)',
          borderRadius: '6px',
          padding: '2px 4px',
          flexShrink: 0
        }}>
          {/* Column picker */}
          <select
            value={filter.column}
            onChange={e => onUpdateAdhocFilter(filter.id, 'column', e.target.value)}
            style={{ ...selectStyle, minWidth: '90px', border: 'none' }}
          >
            <option value="">Column...</option>
            {adhocColumnOptions.map(col => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
          {/* Operator picker */}
          <select
            value={filter.operator}
            onChange={e => onUpdateAdhocFilter(filter.id, 'operator', e.target.value)}
            style={{ ...selectStyle, width: '80px', border: 'none' }}
          >
            {FILTER_OPERATORS.map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>
          {/* Value input */}
          <input
            value={filter.value}
            onChange={e => onUpdateAdhocFilter(filter.id, 'value', e.target.value)}
            placeholder="value"
            style={{ ...inputStyle, border: 'none', width: '80px' }}
          />
          {/* Remove button */}
          <button
            onClick={() => onRemoveAdhocFilter(filter.id)}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.textMuted,
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '3px'
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; }}
          >
            <X size={12} />
          </button>
        </div>
      ))}

      {/* Add Filter button */}
      <button
        onClick={onAddAdhocFilter}
        title="Add ad-hoc filter"
        style={{
          padding: '4px 8px',
          background: 'transparent',
          border: `1px dashed ${theme.border}`,
          borderRadius: '6px',
          color: theme.textMuted,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
          fontWeight: '600',
          flexShrink: 0,
          height: '26px'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = '#6366f1';
          e.currentTarget.style.color = '#6366f1';
          e.currentTarget.style.borderStyle = 'solid';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = theme.border;
          e.currentTarget.style.color = theme.textMuted;
          e.currentTarget.style.borderStyle = 'dashed';
        }}
      >
        <Filter size={11} />
        Add Filter
      </button>

      {/* Right-side actions */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {hasVariables && (
          <button
            onClick={onRefreshVariables}
            title="Refresh variable options"
            style={{
              padding: '4px',
              background: 'transparent',
              border: 'none',
              color: theme.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '4px'
            }}
            onMouseEnter={e => { e.currentTarget.style.color = theme.text; }}
            onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; }}
          >
            <RefreshCw size={13} />
          </button>
        )}
        {onShare && (
        <button
          onClick={onShare}
          title="Share dashboard"
          style={{
            padding: '4px 10px',
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            color: theme.textMuted,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            fontWeight: '600',
            height: '26px'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#6366f1';
            e.currentTarget.style.color = '#6366f1';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = theme.border;
            e.currentTarget.style.color = theme.textMuted;
          }}
        >
          <Share2 size={12} />
          Share
        </button>
        )}
      </div>
    </div>
  );
}
